import { createOptions } from 'utils/commonUtils'
import SelectDamagedPartGroup from 'components/elements/form/DamagedPartGroup/SelectDamagedPartGroup'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import SelectDamagedImageGroup from 'components/elements/form/SelectDamagedImageGroup'
import SwitchButtonGroup from 'components/elements/form/SwitchButtonGroup'
import PhotoPointNote from 'components/elements/items/PhotoPointNote'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import KsSelectDestinationContent from 'components/elements/items/KsSelectDestinationContent'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import React, { useEffect } from 'react'
import {
  AccidentImage,
  AccidentImageKeys,
  AccidentPartInfo,
  AccidentStatusTypes,
  DestinationTypes,
  ImageInfo,
  initialQnEditState,
  QnStatesProps,
  VehicleHeightStatusTypes,
} from 'states/QnState'
import {
  StatesProps,
  useAddressMatch,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import { isSjDomain, isKsDomain, isSaficDomain, getDomain } from 'utils/domain'
import { success } from 'utils/status'
import KsSelectVehicleModificationContent from 'components/elements/items/KsSelectVehicleModificationContent'
import DOMAIN from 'constants/domains'

const accidentStatusOptions = createOptions(
  VEHICLE_STATE_FORM.accidentStatus.options
)

/**
 * QN-03-d: 事故
 */
const Accident: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const {
    accidentImage,
    accidentPart,
    noPhoto,
    destinationType,
    isNotApplicableParts,
    vehicleHeightStatus,
    accidentStatus,
  } = editState.vehicleStateInfo

  /** 配送先とトラブル場所のバリデーション */
  const addressMatchError = useAddressMatch(editState)

  // バリデーション
  const isDisabled = React.useMemo(() => {
    const hasAccidentEvidence = noPhoto
      ? // 組み合わせの損傷箇所が一つでも選択されていればtrue
        accidentPart.parts.length > 0
      : // 必須の画像選択なしでfalse
        !!accidentImage.damagedPart.image && !!accidentImage.peripheral.image

    if (destinationType === DestinationTypes.specify) {
      // 写真以外で状況説明 & ご存知のディーラー・修理工場など予定
      if (
        // タイヤ軸が複数ある車両でないかつ、事故証拠がない もしくは搬送先指定なしの場合はtrue
        (!isNotApplicableParts && !hasAccidentEvidence) ||
        !editState.destinationLocation.isSelected
      ) {
        return true
      }
      // 配送先とトラブル場所が同一の場合はtrue
      return addressMatchError
    } else {
      // タイヤ軸が複数ない & 事故画像なし or 損傷個所なしはtrue
      return !isNotApplicableParts && !hasAccidentEvidence
    }
  }, [editState, addressMatchError])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    switch (editState.vehicleStateInfo.destinationType) {
      case DestinationTypes.specify:
        return isKsDomain() ? WARNING.WRECKER_KS : WARNING.WRECKER
      case DestinationTypes.introduction:
        if (
          // 共済系ドメイン時 かつ「入庫先紹介希望」選択時
          isKsDomain()
        ) {
          return ''
        }
        return WARNING.INTRODUCTION
    }
    return ''
  }, [editState.vehicleStateInfo.destinationType])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo(() => {
    const transitionDestination = isKsDomain()
      ? PAGE_PATH.CONTRACT_INFORMATION
      : isSaficDomain()
      ? PAGE_PATH.SERVICE_INFORMATION
      : {
          [DOMAIN.SJ]: PAGE_PATH.CONTRACT_INFORMATION,
        }[getDomain()]
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType, getDomain()])

  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, imageInfo: ImageInfo) => {
      const accidentImage: AccidentImage = {
        ...editState.vehicleStateInfo.accidentImage,
      }
      accidentImage[name] = imageInfo
      setVehicleStateInfo({
        accidentImage,
      })
    },
    [editState, setVehicleStateInfo]
  )

  const handleNoPhotoChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        noPhoto: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleAccidentStatusChanged = React.useCallback(
    (value: AccidentStatusTypes) => {
      setVehicleStateInfo({
        accidentStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleDamagedPartChanged = React.useCallback(
    (value: AccidentPartInfo): void => {
      setVehicleStateInfo({
        accidentPart: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleIsMotorbikeChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isMotorbike: value,
        accidentPart: {
          ...initialQnEditState.vehicleStateInfo.accidentPart,
        },
      })
    },
    [setVehicleStateInfo]
  )

  const handleIsNotApplicablePartsChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isNotApplicableParts: value,
      })
    },
    [setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    setEditingItem({
      ...editState,
      vehicleStateInfo: state.vehicleStateInfo,
      troubleDatetime: state.troubleDatetime,
      vehicleState: state.vehicleState,
      destinationLocation: state.destinationLocation,
    })
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.destinationLocation,
  ])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setVehicleStateInfo({
      accidentStatus:
        accidentStatus === '' ? AccidentStatusTypes.car : accidentStatus,
      destinationType:
        destinationType === '' ? DestinationTypes.specify : destinationType,
      vehicleHeightStatus:
        vehicleHeightStatus === ''
          ? VehicleHeightStatusTypes.moreThan9cm
          : vehicleHeightStatus,
    })
  }, [])

  return (
    <RequestTemplate
      title={'事故'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <SelectDamagedImageGroup
            accidentImage={editState.vehicleStateInfo.accidentImage}
            title={`${VEHICLE_STATE_FORM.accidentImage.formName}を2枚撮影`}
            isRequired={!editState.vehicleStateInfo.noPhoto}
            onChanged={handleImageChanged}
          />
          <SwitchButtonGroup
            title={`${VEHICLE_STATE_FORM.noPhoto.formName}は選択`}
            isSwitched={editState.vehicleStateInfo.noPhoto}
            label={`${VEHICLE_STATE_FORM.noPhoto.label}`}
            onChanged={handleNoPhotoChanged}
          />
          <PhotoPointNote />
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.accidentStatus.formName}を選択`}
            isRequired={true}
            options={accidentStatusOptions}
            selectedValue={editState.vehicleStateInfo.accidentStatus}
            onValueChanged={handleAccidentStatusChanged}
          />
          {isKsDomain() ? (
            <KsSelectVehicleModificationContent />
          ) : (
            <SelectVehicleModificationContent />
          )}
          {isKsDomain() ? (
            <KsSelectDestinationContent isIntroduction={true} />
          ) : (
            <SelectDestinationContent />
          )}
          {editState.vehicleStateInfo.noPhoto && (
            <>
              <SelectDamagedPartGroup
                title={`${VEHICLE_STATE_FORM.accidentPart.formName}を選択（複数選択可）`}
                isRequired={!isNotApplicableParts}
                damagedStatus={{
                  parts: editState.vehicleStateInfo.accidentPart.parts,
                  existsDamagedTire:
                    editState.vehicleStateInfo.accidentPart.existsDamagedTire,
                }}
                existsDamagedTireLabel={`${VEHICLE_STATE_FORM.accidentPart.label}`}
                isMotorbike={editState.vehicleStateInfo.isMotorbike}
                onChanged={(value: AccidentPartInfo): void => {
                  handleDamagedPartChanged(value)
                }}
              />
              {(isSjDomain() || isKsDomain()) && (
                <>
                  <SwitchButtonGroup
                    title={`${VEHICLE_STATE_FORM.motorbikeAccident.formName}`}
                    isSwitched={editState.vehicleStateInfo.isMotorbike}
                    label={`${VEHICLE_STATE_FORM.motorbikeAccident.label}`}
                    onChanged={handleIsMotorbikeChanged}
                  />
                  <SwitchButtonGroup
                    title={`${VEHICLE_STATE_FORM.notApplicableParts.formName}`}
                    isSwitched={editState.vehicleStateInfo.isNotApplicableParts}
                    label={`${VEHICLE_STATE_FORM.notApplicableParts.label}`}
                    onChanged={handleIsNotApplicablePartsChanged}
                  />
                </>
              )}
            </>
          )}
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      notClearEditingItem
      isDisabled={isDisabled}
    />
  )
}

export default Accident
